import { FunctionComponent, ReactNode, useRef, useState } from "react";
import TextFieldAtom from "../../../atoms/textfield";
import styles from "./PhoneNumber.module.css";
import {
  LabelPairedChevronDownSmBoldIcon,
  LabelPairedMobileNotchLgBoldIcon,
} from "@deriv/quill-icons";
import { useClickOutside } from "../../../../src/hooks/useClickOutside";
import DropdownContainerAtom, {
  DropdownDataType,
  DropdownItemType,
} from "../../../../src/atoms/dropdown/container";
import { usePortalClick } from "../../../hooks/usePortalClick";

export type PhoneNumberType = {
  className?: string;
  label?: string;
  labelLeft?: string;
  labelLeftValue?: string;
  placeholder?: string;
  message?: string;

  /** Variant props */
  indicator?: "neutral" | "fail" | "success";
  state?: "default" | "disabled";
  type?: "outline" | "fill";
  size?: "lg" | "md" | "sm";
  textAlignment?: "left" | "center";

  /** Custom  input props */
  itemSize?: "md" | "sm";
  iconRight?: ReactNode;
  isRequired?: boolean;
  optionalText?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
  customContent?: ReactNode;
  onChange?: (item: DropdownItemType | DropdownItemType[]) => void;
  onClick?: () => void;
  variant?: "labelled" | "labelless";
  value?: string | number;

  /**Custom Dropdown props */
  data?: DropdownDataType[];
  selected?: DropdownItemType;
};

export const PhoneNumber: FunctionComponent<PhoneNumberType> = ({
  className,
  label = "Phone number",
  labelLeft = "Code",
  labelLeftValue = "+00",
  placeholder = "Select an option",
  message,
  indicator,
  state,
  type,
  size = "md",
  itemSize = "md",
  textAlignment = "left",
  isRequired,
  optionalText,
  containerRef,
  iconRight,
  selected: internalSelected,
  onChange,
  onClick,
  data = [],
  variant = "labelled",
  value,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const defaultRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = containerRef || defaultRef;
  const [code, setCode] = useState<string>("+00");
  const [selected, setSelected] = useState<DropdownItemType | undefined>(
    internalSelected
  );
  const handleClick = (e: React.MouseEvent) => {
    const { isPortalClick } = usePortalClick(
      dropdownRef as React.RefObject<HTMLElement>
    );

    if (isPortalClick(e) || state === "disabled") {
      return;
    }

    setIsOpen(!isOpen);
    onClick?.();
  };

  useClickOutside(dropdownRef, () => setIsOpen(false), [
    "quill-ui-next-dropdown-container",
  ]);

  return (
    <TextFieldAtom
      ref={inputRef}
      containerRef={dropdownRef as React.RefObject<HTMLDivElement>}
      label={label}
      labelLeft={labelLeft}
      labelLeftValue={code || labelLeftValue}
      placeholder={placeholder}
      message={message}
      inputType="number"
      className={`quill-ui-next-pointer ${styles.root} ${className}`}
      onAddonLeftClick={(e) => {
        (document.activeElement as HTMLElement)?.blur();
        handleClick(e);
      }}
      iconLeft={<LabelPairedMobileNotchLgBoldIcon />}
      iconRight={iconRight}
      iconLeftSecondary={
        <LabelPairedChevronDownSmBoldIcon
          className={`quill-ui-next-rotatable ${
            isOpen && "quill-ui-next-rotate"
          }`}
        />
      }
      isLeftGroup={true}
      indicator={indicator}
      state={state}
      type={type}
      size={size}
      textAlignment={textAlignment}
      isRequired={isRequired}
      optionalText={optionalText}
      variant={variant}
      value={value}
      customContent={
        <DropdownContainerAtom
          width="100%"
          textAlignment={textAlignment}
          parent={dropdownRef}
          isOpen={isOpen}
          itemSize={itemSize}
          data={data}
          containerSize="md"
          selected={selected}
          onChange={(e: DropdownItemType | DropdownItemType[]) => {
            onChange?.(e);
            const selectedItem = Array.isArray(e) ? e[0] : e;
            setSelected(selectedItem);
            setCode(selectedItem.value);
            setIsOpen(false);
            inputRef.current?.focus();
          }}
        />
      }
    />
  );
};

PhoneNumber.displayName = "PhoneNumber";

export default PhoneNumber;
